/**
 * Typography Scale System
 * 
 * Industry-standard responsive typography scale
 * Pattern: GitHub, Linear, Stripe, Vercel
 * 
 * Breakpoints:
 * - Base (default): Mobile-first (< 640px)
 * - sm: 640px+
 * - md: 768px+
 * - lg: 1024px+
 * - xl: 1280px+
 */

export const typographyScale = {
  // Headings - Responsive sizes across breakpoints
  h1: 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight',
  h2: 'text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight tracking-tight',
  h3: 'text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold leading-snug',
  h4: 'text-base sm:text-lg md:text-xl lg:text-2xl font-semibold leading-snug',
  h5: 'text-sm sm:text-base md:text-lg lg:text-xl font-semibold leading-normal',
  h6: 'text-xs sm:text-sm md:text-base lg:text-lg font-semibold leading-normal',

  // Body text - Responsive sizes
  body: {
    xs: 'text-xs sm:text-xs md:text-sm leading-relaxed',
    sm: 'text-sm sm:text-sm md:text-base leading-relaxed',
    base: 'text-base sm:text-base md:text-lg leading-relaxed',
    lg: 'text-lg sm:text-lg md:text-xl leading-relaxed',
    xl: 'text-xl sm:text-xl md:text-2xl leading-relaxed',
  },

  // Font weights
  weight: {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  },

  // Line heights
  lineHeight: {
    tight: 'leading-tight',
    snug: 'leading-snug',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
    loose: 'leading-loose',
  },

  // Letter spacing
  tracking: {
    tighter: 'tracking-tighter',
    tight: 'tracking-tight',
    normal: 'tracking-normal',
    wide: 'tracking-wide',
    wider: 'tracking-wider',
    widest: 'tracking-widest',
  },
} as const;

/**
 * Get heading class by level
 */
export const getHeadingClass = (level: 1 | 2 | 3 | 4 | 5 | 6): string => {
  return typographyScale[`h${level}` as keyof typeof typographyScale] as string;
};

/**
 * Get body text class by size
 */
export const getBodyClass = (size: 'xs' | 'sm' | 'base' | 'lg' | 'xl' = 'base'): string => {
  return typographyScale.body[size];
};

/**
 * Combine typography utilities
 */
export const combineTypography = (...classes: string[]): string => {
  return classes.filter(Boolean).join(' ');
};

